/**
 * StudioCanvasOverlay — direct manipulation on top of the live Remotion
 * Player (Phase 2 of the Studio iterate editor).
 *
 * Sits absolutely over the player box and:
 *   1. MEASURES the real rendered elements each animation frame via the
 *      inert `data-studio-el` seams (caption / logo / speaker) that the
 *      composition components expose — so selection chrome hugs the exact
 *      pixels the render will produce, fonts and wraps included.
 *   2. HIT-TESTS pointer-downs against those rects (composition z-order:
 *      logo over caption over speaker) → click selects, drag moves.
 *   3. Maps pointer deltas from screen px into the 2160×3840 canvas space
 *      (scale = overlayWidth / 2160; the player box is exactly 9:16) and
 *      emits typed EditPatches: a LIVE patch per rAF while the gesture is
 *      in flight (instant preview), then ONE committed journal entry on
 *      pointer-up — undo undoes a whole gesture, not a pixel.
 *   4. Renders the same SelectionChrome as the onboarding LayoutEditor,
 *      with functional corner handles on logo (widthPct) and speaker card
 *      (scale). Caption is vertical-move only; its sizes live in the
 *      inspector (per-line font sizes).
 *
 * Coordinate model per element (matches the composition CSS exactly):
 *   caption  — vertical only. justify "flex-end": Δbottom = -Δy·s.
 *              justify "center": the block centers in [0, H-bottomPx], so
 *              its centre sits at (H-bottomPx)/2 → Δbottom = -2·Δy·s keeps
 *              the block glued to the pointer.
 *   logo     — xPct is the logo's CENTRE (% of width), bottomPx from the
 *              bottom edge. Δx → ΔxPct = Δx·s/W·100; Δbottom = -Δy·s.
 *   speaker  — top-left anchored xPct/yPct (% of canvas). Straight deltas.
 * (s = canvasPx per screenPx = CANVAS_WIDTH / overlayWidth.)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  baselineLayout,
  type EditPatch,
} from "../../../src/lib/clipEditState.ts";
import { CANVAS_HEIGHT, CANVAS_WIDTH, type ClipProps } from "../../../src/remotion/types.ts";
import { NO_SELECTION, type StudioElementId, type StudioSelection } from "../lib/studioTypes";
import { SelectionChrome, type ResizeHandlerBag } from "./LayoutEditor";

const ACCENT = "#FFD500";
/** Pointer must travel this many screen px before a press becomes a drag. */
const DRAG_THRESHOLD_PX = 3;
/** Ignore near-invisible elements (speaker card in its post-fade dwell). */
const MIN_VISIBLE_OPACITY = 0.05;

/**
 * Does this seam actually PAINT anything?
 *
 * True when it carries visible text (a live-drawn card), or at least one image
 * that finished loading with real pixels. An `<img>` that 404s or throws
 * `EncodingError` reports `complete === true` with `naturalWidth === 0`, and
 * that is exactly the invisible-but-draggable case.
 */
function drawsSomething(node: HTMLElement): boolean {
  if ((node.textContent ?? "").trim().length > 0) return true;
  const imgs = node.querySelectorAll("img");
  if (imgs.length === 0) return false;
  for (const img of imgs) {
    if (!img.complete) return true; // still loading — don't judge it yet
    if (img.naturalWidth > 0 && img.naturalHeight > 0) return true;
  }
  return false;
}

interface ElRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

type RectMap = Partial<Record<StudioElementId, ElRect>>;

/** Topmost-first hit order = reverse composition mount order. */
const HIT_ORDER: StudioElementId[] = ["logo", "caption", "speaker"];

const round2 = (v: number): number => Math.round(v * 100) / 100;
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** The caption word under the pointer, found by testing each word's own range
 *  rects — geometry only, so it works through the overlay sitting on top. The
 *  DOM-order index doubles as a stable "which word" handle. Null when the click
 *  landed between words. (Tweak Mode phase 2: lets "make THIS word gold" target
 *  one word.) */
function captionWordAtPoint(capNode: HTMLElement, x: number, y: number): { word: string; wordIdx: number } | null {
  const walker = document.createTreeWalker(capNode, NodeFilter.SHOW_TEXT);
  let wordIdx = 0;
  for (let tn = walker.nextNode(); tn; tn = walker.nextNode()) {
    const s = tn.textContent ?? "";
    const re = /\S+/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(s)) !== null) {
      const range = document.createRange();
      range.setStart(tn, m.index);
      range.setEnd(tn, m.index + m[0].length);
      const rects = range.getClientRects();
      for (let i = 0; i < rects.length; i++) {
        const r = rects[i]!;
        if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
          return { word: m[0], wordIdx };
        }
      }
      wordIdx++;
    }
  }
  return null;
}

function rectsDiffer(a: RectMap, b: RectMap): boolean {
  for (const el of HIT_ORDER) {
    const ra = a[el];
    const rb = b[el];
    if (!ra !== !rb) return true;
    if (ra && rb) {
      if (
        Math.abs(ra.left - rb.left) > 0.5 ||
        Math.abs(ra.top - rb.top) > 0.5 ||
        Math.abs(ra.width - rb.width) > 0.5 ||
        Math.abs(ra.height - rb.height) > 0.5
      ) {
        return true;
      }
    }
  }
  return false;
}

interface DragState {
  el: StudioElementId;
  pointerId: number;
  startClientX: number;
  startClientY: number;
  /** canvas px per screen px at gesture start. */
  canvasPerScreen: number;
  /** The dragged element's measured rect at gesture start (overlay-root
   *  coords) — smart-guide snapping compares its pointer-translated centre
   *  against the frame centre. Null = element unmeasured, snapping off. */
  startRect: ElRect | null;
  /** Overlay box size at gesture start (frame centre = half of these). */
  rootW: number;
  rootH: number;
  /** Snapshot of the concrete values the gesture starts from. */
  start: {
    capBottomPx: number;
    capJustify: "center" | "flex-end";
    logoXPct: number;
    logoBottomPx: number;
    cardXPct: number;
    cardYPct: number;
  };
  moved: boolean;
  lastPatch: EditPatch | null;
  lastLabel: string;
}

/** Smart-guide snap: the dragged element's centre within this many SCREEN px
 *  of the frame centre magnetises to it (and must travel past it to escape —
 *  the "resistance" feel). */
const SNAP_ENGAGE_PX = 8;

/** Elements that visually translate 1:1 with the pointer on each axis. The
 *  caption is a full-width block — only its Y participates. */
const SNAPPABLE_X: Record<StudioElementId, boolean> = { caption: false, logo: true, speaker: true, video: false };

interface ResizeState {
  el: "logo" | "speaker";
  /** Element centre in client coords at gesture start. */
  cx: number;
  cy: number;
  startDist: number;
  startWidthPct: number;
  startScale: number;
  lastPatch: EditPatch | null;
  lastLabel: string;
}

/** Caption side-handle gesture (phase 2 — the CapCut side-drag): dragging
 *  an edge handle edits sidePaddingPx symmetrically, re-wrapping the text
 *  live (auto-wrap turns on with the first such gesture, same rule as the
 *  inspector's width fields). */
interface SideDragState {
  side: "left" | "right";
  startClientX: number;
  canvasPerScreen: number;
  startPaddingPx: number;
  /** wrapMode was never set → this gesture also enables auto-wrap. */
  wrapUnset: boolean;
  lastPatch: EditPatch | null;
  lastLabel: string;
}

export interface StudioCanvasOverlayProps {
  /** Effective (edit-state-applied) props currently feeding the Player —
   *  gestures snapshot their start values from here. */
  effective: ClipProps;
  selection: StudioSelection;
  onSelect: (sel: StudioSelection) => void;
  /** Mid-gesture preview patch (null clears). */
  onLive: (patch: EditPatch | null) => void;
  /** Gesture finished → append ONE journal entry (source "canvas"). */
  onCommit: (patch: EditPatch, label: string) => void;
  /** Cue index active at the playhead — clicking the caption selects it. */
  activeCueIdx: number;
}

export function StudioCanvasOverlay(props: StudioCanvasOverlayProps): React.JSX.Element {
  const { effective, selection, onSelect, onLive, onCommit, activeCueIdx } = props;
  const rootRef = useRef<HTMLDivElement>(null);
  const [rects, setRects] = useState<RectMap>({});
  const rectsRef = useRef<RectMap>({});
  const [hovered, setHovered] = useState<StudioElementId | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const resizeRef = useRef<ResizeState | null>(null);
  const [gestureActive, setGestureActive] = useState(false);
  // Smart guides: which centre axes the drag is currently snapped to. State
  // drives the guide lines; the ref avoids re-render churn per rAF and lets
  // us haptic-buzz exactly on the engage edge.
  const [snapAxes, setSnapAxes] = useState<{ x: boolean; y: boolean }>({ x: false, y: false });
  const snapRef = useRef({ x: false, y: false });
  const updateSnapAxes = useCallback((x: boolean, y: boolean): void => {
    const prev = snapRef.current;
    if (prev.x === x && prev.y === y) return;
    // Vibrate on the transition INTO a snap (supported devices only — Android
    // Chrome etc.; elsewhere the magnetic resistance itself is the feedback).
    if ((x && !prev.x) || (y && !prev.y)) {
      try { navigator.vibrate?.(10); } catch { /* unsupported */ }
    }
    snapRef.current = { x, y };
    setSnapAxes(snapRef.current);
  }, []);
  const rafMoveRef = useRef<number | null>(null);
  const lastPtrRef = useRef<{ x: number; y: number } | null>(null);

  // Fresh snapshot for gesture starts without re-binding handlers.
  const effectiveRef = useRef(effective);
  effectiveRef.current = effective;
  const activeCueRef = useRef(activeCueIdx);
  activeCueRef.current = activeCueIdx;

  // ---- 1. Measurement loop: track the real rendered rects every frame ----
  useEffect(() => {
    let raf = 0;
    const tick = (): void => {
      const root = rootRef.current;
      const host = root?.parentElement;
      if (root && host) {
        const rootRect = root.getBoundingClientRect();
        const next: RectMap = {};
        for (const el of HIT_ORDER) {
          const node = host.querySelector<HTMLElement>(`[data-studio-el="${el}"]`);
          if (!node) continue;
          // Low-opacity skip is for the SPEAKER card's post-fade dwell only:
          // captions dip to 0 at every entrance-animation start (and a logo
          // can be styled near-transparent) yet must stay selectable —
          // Studio cue-clicks seek to exactly those frames (critic-caught).
          if (el === "speaker") {
            const opacity = Number(window.getComputedStyle(node).opacity);
            if (Number.isFinite(opacity) && opacity < MIN_VISIBLE_OPACITY) continue;
            // A baked card whose PNG 404s or fails to decode leaves a seam of
            // the right SIZE but nothing drawn in it — so the operator could
            // select and drag an object they cannot see (QA D-7). No live text
            // and no image that actually decoded ⇒ there is nothing there.
            if (!drawsSomething(node)) continue;
          }
          const r = node.getBoundingClientRect();
          if (r.width < 1 || r.height < 1) continue;
          next[el] = {
            left: r.left - rootRect.left,
            top: r.top - rootRect.top,
            width: r.width,
            height: r.height,
          };
        }
        if (rectsDiffer(rectsRef.current, next)) {
          rectsRef.current = next;
          setRects(next);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const hitTest = useCallback((clientX: number, clientY: number): StudioElementId | null => {
    const root = rootRef.current;
    if (!root) return null;
    const rootRect = root.getBoundingClientRect();
    const x = clientX - rootRect.left;
    const y = clientY - rootRect.top;
    for (const el of HIT_ORDER) {
      const r = rectsRef.current[el];
      if (r && x >= r.left && x <= r.left + r.width && y >= r.top && y <= r.top + r.height) {
        return el;
      }
    }
    return null;
  }, []);

  // ---- 2/3. Drag: pointer deltas → canvas-space patches ----
  const applyDragMove = useCallback((): void => {
    rafMoveRef.current = null;
    const d = dragRef.current;
    const p = lastPtrRef.current;
    if (!d || !p) return;
    let dxScreen = p.x - d.startClientX;
    let dyScreen = p.y - d.startClientY;
    if (!d.moved && Math.hypot(dxScreen, dyScreen) < DRAG_THRESHOLD_PX) return;
    d.moved = true;

    // ---- Smart guides: magnetise the element's centre to the frame centre.
    // Every draggable element translates 1:1 with the pointer on screen, so
    // the would-be centre is just the start rect's centre plus the deltas.
    // Snapping ADJUSTS the deltas (the element sticks; the pointer must
    // travel past the engage zone to break free — the resistance feel).
    let snapX = false;
    let snapY = false;
    if (d.startRect) {
      const cx = d.startRect.left + d.startRect.width / 2 + dxScreen;
      const cy = d.startRect.top + d.startRect.height / 2 + dyScreen;
      if (SNAPPABLE_X[d.el] && Math.abs(cx - d.rootW / 2) <= SNAP_ENGAGE_PX) {
        dxScreen += d.rootW / 2 - cx;
        snapX = true;
      }
      if (Math.abs(cy - d.rootH / 2) <= SNAP_ENGAGE_PX) {
        dyScreen += d.rootH / 2 - cy;
        snapY = true;
      }
    }
    updateSnapAxes(snapX, snapY);
    const dx = dxScreen * d.canvasPerScreen;
    const dy = dyScreen * d.canvasPerScreen;
    /** Element size in canvas px — for computing the EXACT dead-centre value
     *  on a snapped axis (no rounding drift from screen measurement). */
    const wC = (d.startRect?.width ?? 0) * d.canvasPerScreen;
    const hC = (d.startRect?.height ?? 0) * d.canvasPerScreen;
    const centred = snapX || snapY ? " · ⌖ centered" : "";

    let patch: EditPatch;
    let label: string;
    if (d.el === "caption") {
      // justify "center": block centre sits at (H - bottomPx)/2 → factor 2
      // keeps the block glued to the pointer. "flex-end": 1:1.
      const factor = d.start.capJustify === "center" ? 2 : 1;
      let bottomPx = Math.round(clamp(d.start.capBottomPx - factor * dy, 0, CANVAS_HEIGHT - 400));
      if (snapY && d.startRect) {
        // Dead centre: flex-end pins the block bottomPx above the bottom edge;
        // justify-center's block centre is (H - bottomPx)/2 → centre = 0.
        bottomPx = d.start.capJustify === "center" ? 0 : Math.round(CANVAS_HEIGHT / 2 - hC / 2);
      }
      patch = { layout: { caption: { bottomPx } } };
      label = `Moved caption → ${bottomPx}px from bottom${centred}`;
    } else if (d.el === "logo") {
      // Logo xPct IS its centre as % of width — snapped means exactly 50.
      const xPct = snapX ? 50 : round2(clamp(d.start.logoXPct + (dx / CANVAS_WIDTH) * 100, 2, 98));
      const bottomPx = snapY && d.startRect
        ? Math.round(CANVAS_HEIGHT / 2 - hC / 2)
        : Math.round(clamp(d.start.logoBottomPx - dy, 0, CANVAS_HEIGHT - 200));
      patch = { layout: { logo: { xPct, bottomPx } } };
      label = `Moved logo → x ${xPct}% · ${bottomPx}px up${centred}`;
    } else {
      // Speaker card is TOP-LEFT anchored — dead centre offsets by half the
      // card's own size.
      const xPct = snapX && d.startRect
        ? round2(((CANVAS_WIDTH - wC) / 2 / CANVAS_WIDTH) * 100)
        : round2(clamp(d.start.cardXPct + (dx / CANVAS_WIDTH) * 100, 0, 95));
      const yPct = snapY && d.startRect
        ? round2(((CANVAS_HEIGHT - hC) / 2 / CANVAS_HEIGHT) * 100)
        : round2(clamp(d.start.cardYPct + (dy / CANVAS_HEIGHT) * 100, 0, 95));
      patch = { layout: { speakerCard: { xPct, yPct } } };
      label = `Moved speaker card → x ${xPct}% · y ${yPct}%${centred}`;
    }
    d.lastPatch = patch;
    d.lastLabel = label;
    onLive(patch);
  }, [onLive, updateSnapAxes]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    const el = hitTest(e.clientX, e.clientY);
    if (!el) {
      onSelect(NO_SELECTION);
      return;
    }
    // Caption click → also resolve the specific word under the pointer so a
    // Tweak can target it ("make THIS word gold").
    const capNode =
      el === "caption"
        ? rootRef.current?.parentElement?.querySelector<HTMLElement>('[data-studio-el="caption"]') ?? null
        : null;
    const wordHit = capNode ? captionWordAtPoint(capNode, e.clientX, e.clientY) : null;
    onSelect({
      el,
      cueIdx: el === "caption" ? (activeCueRef.current >= 0 ? activeCueRef.current : null) : null,
      ...(wordHit ? { word: wordHit.word, wordIdx: wordHit.wordIdx } : {}),
    });
    const root = rootRef.current;
    if (!root) return;
    const rootRect = root.getBoundingClientRect();
    if (rootRect.width === 0) return;
    const layout = baselineLayout(effectiveRef.current);
    dragRef.current = {
      el,
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      canvasPerScreen: CANVAS_WIDTH / rootRect.width,
      startRect: rectsRef.current[el] ?? null,
      rootW: rootRect.width,
      rootH: rootRect.height,
      start: {
        capBottomPx: layout.caption.bottomPx,
        capJustify: layout.caption.justify,
        logoXPct: layout.logo.xPct,
        logoBottomPx: layout.logo.bottomPx,
        cardXPct: layout.speakerCard.xPct,
        cardYPct: layout.speakerCard.yPct,
      },
      moved: false,
      lastPatch: null,
      lastLabel: "",
    };
    try {
      root.setPointerCapture(e.pointerId);
    } catch {
      /* older browsers */
    }
    setGestureActive(true);
  }, [hitTest, onSelect]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    if (dragRef.current) {
      lastPtrRef.current = { x: e.clientX, y: e.clientY };
      if (rafMoveRef.current == null) rafMoveRef.current = requestAnimationFrame(applyDragMove);
      return;
    }
    // Idle: hover feedback.
    const el = hitTest(e.clientX, e.clientY);
    setHovered((h) => (h === el ? h : el));
  }, [applyDragMove, hitTest]);

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    const d = dragRef.current;
    if (!d) return;
    // FLUSH the pending move BEFORE tearing the gesture down.
    //
    // The translation is computed in a `requestAnimationFrame` (one patch per
    // painted frame, not per pointer event). A gesture whose pointerup lands
    // in the SAME frame as its last pointermove — a quick flick, and every
    // programmatic drag — used to cancel that frame on the way out, so
    // `d.moved` was still false and `endDrag` committed nothing: the caption
    // did not move, no journal entry appeared, and the press read as a plain
    // click that only cleared the selection (QA 2026-09-16, M3).
    if (rafMoveRef.current != null) {
      cancelAnimationFrame(rafMoveRef.current);
      rafMoveRef.current = null;
      applyDragMove();
    }
    dragRef.current = null;
    updateSnapAxes(false, false);
    try {
      rootRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    setGestureActive(false);
    if (d.moved && d.lastPatch) {
      onCommit(d.lastPatch, d.lastLabel);
    } else {
      onLive(null); // pure click — nothing moved
    }
  }, [applyDragMove, onCommit, onLive, updateSnapAxes]);

  // ---- 4. Resize handles (logo width, speaker scale) ----
  const startResize = useCallback((el: "logo" | "speaker", _handle: string, e: React.PointerEvent<HTMLElement>): void => {
    e.stopPropagation();
    const root = rootRef.current;
    const r = rectsRef.current[el];
    if (!root || !r) return;
    const rootRect = root.getBoundingClientRect();
    const cx = rootRect.left + r.left + r.width / 2;
    const cy = rootRect.top + r.top + r.height / 2;
    const layout = baselineLayout(effectiveRef.current);
    resizeRef.current = {
      el,
      cx,
      cy,
      startDist: Math.max(4, Math.hypot(e.clientX - cx, e.clientY - cy)),
      startWidthPct: layout.logo.widthPct,
      startScale: layout.speakerCard.scale,
      lastPatch: null,
      lastLabel: "",
    };
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* older browsers */
    }
    setGestureActive(true);
  }, []);

  const onResizeMove = useCallback((e: React.PointerEvent<HTMLElement>): void => {
    const r = resizeRef.current;
    if (!r) return;
    const ratio = Math.hypot(e.clientX - r.cx, e.clientY - r.cy) / r.startDist;
    let patch: EditPatch;
    let label: string;
    if (r.el === "logo") {
      const widthPct = round2(clamp(r.startWidthPct * ratio, 4, 90));
      patch = { layout: { logo: { widthPct } } };
      label = `Resized logo → ${widthPct}% wide`;
    } else {
      const scale = round2(clamp(r.startScale * ratio, 0.3, 3));
      patch = { layout: { speakerCard: { scale } } };
      label = `Resized speaker card → ${Math.round(scale * 100)}%`;
    }
    r.lastPatch = patch;
    r.lastLabel = label;
    onLive(patch);
  }, [onLive]);

  const endResize = useCallback((e: React.PointerEvent<HTMLElement>): void => {
    const r = resizeRef.current;
    if (!r) return;
    resizeRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    setGestureActive(false);
    if (r.lastPatch) onCommit(r.lastPatch, r.lastLabel);
    else onLive(null);
  }, [onCommit, onLive]);

  const resizeBag = useCallback((el: "logo" | "speaker"): ResizeHandlerBag => ({
    onDown: (h, e) => startResize(el, h, e),
    onMove: onResizeMove,
    onUp: endResize,
  }), [startResize, onResizeMove, endResize]);

  // ---- 4b. Caption side handles: drag an edge → sidePaddingPx + re-wrap ----
  const sideDragRef = useRef<SideDragState | null>(null);
  const startSideDrag = useCallback((side: "left" | "right", e: React.PointerEvent<HTMLElement>): void => {
    e.stopPropagation();
    if (e.button !== 0) return; // left button only, like every other gesture
    const root = rootRef.current;
    if (!root) return;
    const rootRect = root.getBoundingClientRect();
    if (rootRect.width === 0) return;
    const layout = baselineLayout(effectiveRef.current);
    sideDragRef.current = {
      side,
      startClientX: e.clientX,
      canvasPerScreen: CANVAS_WIDTH / rootRect.width,
      startPaddingPx: layout.caption.sidePaddingPx,
      wrapUnset: layout.caption.wrapMode === undefined,
      lastPatch: null,
      lastLabel: "",
    };
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch { /* older browsers */ }
    setGestureActive(true);
  }, []);

  const onSideDragMove = useCallback((e: React.PointerEvent<HTMLElement>): void => {
    const s = sideDragRef.current;
    if (!s) return;
    // A buttonless move = the up event was lost (handle unmounted mid-
    // gesture) — never re-apply patches from a stale snapshot on hover.
    if (e.buttons === 0) return;
    const dxCanvas = (e.clientX - s.startClientX) * s.canvasPerScreen;
    // Dragging the LEFT handle right (or the RIGHT handle left) shrinks the
    // text area — symmetric padding, like CapCut's box-width drag.
    const delta = s.side === "left" ? dxCanvas : -dxCanvas;
    const sidePaddingPx = Math.round(clamp(s.startPaddingPx + delta, 0, 800));
    const patch: EditPatch = {
      layout: {
        caption: { sidePaddingPx, ...(s.wrapUnset ? { wrapMode: "auto" as const } : {}) },
      },
    };
    s.lastPatch = patch;
    s.lastLabel = `Caption width dragged → side padding ${sidePaddingPx}px${s.wrapUnset ? " · auto-wrap ON" : ""}`;
    onLive(patch);
  }, [onLive]);

  const endSideDrag = useCallback((e?: React.PointerEvent<HTMLElement>): void => {
    const s = sideDragRef.current;
    if (!s) return;
    sideDragRef.current = null;
    if (e) {
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch { /* noop */ }
    }
    setGestureActive(false);
    if (s.lastPatch) onCommit(s.lastPatch, s.lastLabel);
    else onLive(null);
  }, [onCommit, onLive]);

  // Window-level safety net: if the handle UNMOUNTS mid-gesture (the
  // caption rect vanishes in a cue gap / entrance-anim start), its pointer
  // capture — and the up event — die with it. The window listener still
  // fires, so the gesture always commits exactly once (critic-caught).
  useEffect(() => {
    if (!gestureActive || !sideDragRef.current) return;
    const winUp = (): void => endSideDrag();
    window.addEventListener("pointerup", winUp);
    window.addEventListener("pointercancel", winUp);
    return () => {
      window.removeEventListener("pointerup", winUp);
      window.removeEventListener("pointercancel", winUp);
    };
  }, [gestureActive, endSideDrag]);

  const cursorFor = (el: StudioElementId | null): string => {
    if (gestureActive) return "grabbing";
    if (!el) return "default";
    return el === "caption" ? "ns-resize" : "grab";
  };

  return (
    <div
      ref={rootRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={() => { if (!dragRef.current) setHovered(null); }}
      style={{
        position: "absolute",
        inset: 0,
        touchAction: "none",
        userSelect: "none",
        cursor: cursorFor(hovered),
        zIndex: 5,
      }}
    >
      {/* ---- Smart guides: centre lines while a drag is snapped. Magenta so
          they never read as the yellow selection chrome; a dot marks the
          frame's dead centre. ---- */}
      {snapAxes.x && (
        <div
          style={{
            position: "absolute", left: "50%", top: 0, bottom: 0, width: 0,
            borderLeft: "1.5px solid #FF2D9C",
            boxShadow: "0 0 6px rgba(255,45,156,.7)",
            pointerEvents: "none", zIndex: 4,
          }}
        />
      )}
      {snapAxes.y && (
        <div
          style={{
            position: "absolute", top: "50%", left: 0, right: 0, height: 0,
            borderTop: "1.5px solid #FF2D9C",
            boxShadow: "0 0 6px rgba(255,45,156,.7)",
            pointerEvents: "none", zIndex: 4,
          }}
        />
      )}
      {(snapAxes.x || snapAxes.y) && (
        <div
          style={{
            position: "absolute", left: "50%", top: "50%",
            width: 8, height: 8, marginLeft: -4, marginTop: -4,
            borderRadius: 999, background: "#FF2D9C",
            border: "1.5px solid rgba(255,255,255,.9)",
            boxShadow: "0 0 8px rgba(255,45,156,.8)",
            pointerEvents: "none", zIndex: 4,
          }}
        />
      )}

      {HIT_ORDER.map((el) => {
        const r = rects[el];
        if (!r) return null;
        const isSelected = selection.el === el;
        const isHovered = hovered === el && !isSelected && !gestureActive;
        if (!isSelected && !isHovered) return null;
        return (
          <div
            key={el}
            style={{
              position: "absolute",
              left: r.left,
              top: r.top,
              width: r.width,
              height: r.height,
              pointerEvents: "none",
              outline: isHovered ? `1.5px dashed rgba(255,213,0,.6)` : undefined,
              outlineOffset: 4,
              borderRadius: 4,
            }}
          >
            {isSelected && (
              <SelectionChrome
                accent={ACCENT}
                resize={el === "logo" || el === "speaker" ? resizeBag(el) : undefined}
              />
            )}
            {isSelected && el === "caption" && (["left", "right"] as const).map((side) => (
              <div
                key={side}
                onPointerDown={(e) => startSideDrag(side, e)}
                onPointerMove={onSideDragMove}
                onPointerUp={endSideDrag}
                onPointerCancel={endSideDrag}
                title="Drag to change the caption width — text re-wraps live"
                style={{
                  position: "absolute",
                  [side]: -14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 10,
                  height: 34,
                  borderRadius: 6,
                  background: ACCENT,
                  border: "1.5px solid rgba(0,0,0,.55)",
                  boxShadow: "0 1px 6px rgba(0,0,0,.5)",
                  cursor: "ew-resize",
                  pointerEvents: "auto",
                  touchAction: "none",
                }}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
